import { Link, useLocation } from "react-router-dom";
import { TreePine, Menu, X, User, LogIn, LogOut, LayoutDashboard, MessageSquare, Shield, UserCircle } from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const location = useLocation();
  const { user, roles, signOut } = useAuth();

  useEffect(() => {
    if (!user) return;
    const fetchUnread = async () => {
      const { count } = await supabase
        .from("messages")
        .select("id", { count: "exact", head: true })
        .eq("receiver_id", user.id)
        .eq("read", false);
      setUnreadCount(count ?? 0);
    };
    fetchUnread();

    const channel = supabase
      .channel("navbar-unread")
      .on("postgres_changes", { event: "*", schema: "public", table: "messages" }, () => {
        fetchUnread();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user]);
  const isAdmin = roles.includes("admin");

  const links = [
    { to: "/", label: "Accueil" },
    { to: "/annonces", label: "Annonces" },
    { to: "/vendre", label: "Vendre mon bois" },
  ];

  return (
    <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b">
      <div className="container flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2">
          <TreePine className="h-7 w-7 text-primary" />
          <span className="font-display text-xl font-bold text-foreground">
            Chimay<span className="text-primary">Bois</span>
          </span>
          <span className="text-[10px] font-bold uppercase tracking-wider bg-primary/15 text-primary px-1.5 py-0.5 rounded-md leading-none">Beta</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`text-sm font-medium transition-colors hover:text-primary ${
                location.pathname === l.to ? "text-primary" : "text-muted-foreground"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <Button variant="ghost" size="sm" asChild className="gap-2 relative">
                <Link to="/messages">
                  <MessageSquare className="h-4 w-4" /> Messages
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </Link>
              </Button>
              <Button variant="ghost" size="sm" asChild className="gap-2">
                <Link to="/tableau-de-bord"><LayoutDashboard className="h-4 w-4" /> Dashboard</Link>
              </Button>
              <Button variant="ghost" size="sm" asChild className="gap-2">
                <Link to="/profil"><UserCircle className="h-4 w-4" /> Profil</Link>
              </Button>
              {isAdmin && (
                <Button variant="ghost" size="sm" asChild className="gap-2">
                  <Link to="/admin"><Shield className="h-4 w-4" /> Admin</Link>
                </Button>
              )}
              <Button variant="ghost" size="sm" className="gap-2" onClick={signOut}>
                <LogOut className="h-4 w-4" /> Déconnexion
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" asChild className="gap-2">
                <Link to="/auth"><LogIn className="h-4 w-4" /> Connexion</Link>
              </Button>
              <Button size="sm" asChild className="gap-2">
                <Link to="/auth"><User className="h-4 w-4" /> Inscription</Link>
              </Button>
            </>
          )}
        </div>

        {/* Mobile toggle */}
        <button onClick={() => setOpen(!open)} className="md:hidden p-2 text-foreground" aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}>
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden border-t bg-background px-4 pb-4 animate-fade-in">
          <nav className="flex flex-col gap-3 pt-3">
            {links.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
                className={`text-sm font-medium py-2 ${location.pathname === l.to ? "text-primary" : "text-muted-foreground"}`}
              >
                {l.label}
              </Link>
            ))}
            {user ? (
              <>
                <Link to="/tableau-de-bord" onClick={() => setOpen(false)} className="text-sm font-medium py-2 text-muted-foreground">Dashboard</Link>
                <Link to="/messages" onClick={() => setOpen(false)} className="text-sm font-medium py-2 text-muted-foreground">Messages</Link>
                <Link to="/profil" onClick={() => setOpen(false)} className="text-sm font-medium py-2 text-muted-foreground">Mon Profil</Link>
                <Button variant="ghost" size="sm" onClick={() => { signOut(); setOpen(false); }}>Déconnexion</Button>
              </>
            ) : (
              <div className="flex gap-3 pt-2">
                <Button variant="ghost" size="sm" asChild className="flex-1 gap-2">
                  <Link to="/auth" onClick={() => setOpen(false)}><LogIn className="h-4 w-4" /> Connexion</Link>
                </Button>
                <Button size="sm" asChild className="flex-1 gap-2">
                  <Link to="/auth" onClick={() => setOpen(false)}><User className="h-4 w-4" /> Inscription</Link>
                </Button>
              </div>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
